import React from "react";
import { Box, Button, Chip, Typography } from "@mui/material";
import CheckIcon from "@mui/icons-material/Check";
import {
  getPlanBillingLabel,
  getPlanFeatures,
  getPlanPriceLabel,
} from "./plans";
import { PRICING_VERSION_V2 } from "./pricingVersion";

/** Same lavender wash + drift as onboarding (OnboardingShell / OnboardingChat). */
const ONBOARDING_GRADIENT =
  "linear-gradient(115deg, #F6F4FF 0%, rgba(74,44,245,0.16) 28%, rgba(232,74,138,0.12) 52%, #F6F4FF 72%, rgba(246,244,255,0) 100%)";

export default function PricingPlanCard({
  plan,
  billingCycle,
  recommended = false,
  onSelect,
  disabled = false,
  visualVersion = "v1",
  offerLabel = "7-day free trial",
}) {
  const priceLabel = getPlanPriceLabel(plan, billingCycle);
  const billingLabel = getPlanBillingLabel(billingCycle);
  const features = getPlanFeatures(plan, billingCycle);

  const isV2 = visualVersion === PRICING_VERSION_V2;
  const isGradientCard = recommended && isV2;

  const colors = {
    title: "#111827",
    description: "#6B7280",
    price: "#111827",
    muted: "#6B7280",
    featuresIntro: "#374151",
    feature: "#4B5563",
    footnote: "#9CA3AF",
    check: "#4A2CF5",
    divider: "#EEF0F4",
    savingsBg: "#F6F4FF",
    savingsText: "#4A2CF5",
    savingsBorder: "rgba(74, 44, 245, 0.14)",
    badgeBg: "#4A2CF5",
    badgeText: "#FFFFFF",
    ctaBg: "#4A2CF5",
    ctaText: "#FFFFFF",
    ctaHover: "#3B23C6",
  };

  return (
    <Box
      data-testid={`pricing-plan-card-${plan.id}`}
      data-recommended={recommended ? "true" : "false"}
      data-visual-version={visualVersion}
      data-gradient={isGradientCard ? "true" : "false"}
      sx={{
        position: "relative",
        flex: "1 1 0",
        minWidth: 0,
        borderRadius: "10px",
        isolation: "isolate",
        overflow: "hidden",
        bgcolor: isGradientCard ? "#FFFFFF" : "transparent",
        ...(recommended && !isGradientCard
          ? {
              p: "2px",
              boxShadow: "0 4px 14px rgba(74, 44, 245, 0.12)",
              "&::before": {
                content: '""',
                position: "absolute",
                top: "50%",
                left: "50%",
                width: "220%",
                aspectRatio: "1",
                background:
                  "conic-gradient(from 0deg, #4A2CF5 0deg, #775EFF 90deg, #E84A8A 180deg, #775EFF 270deg, #4A2CF5 360deg)",
                transform: "translate(-50%, -50%)",
                animation: "pricingGradientStrokeSpin 3.2s linear infinite",
                zIndex: 0,
              },
              "@keyframes pricingGradientStrokeSpin": {
                to: { transform: "translate(-50%, -50%) rotate(360deg)" },
              },
              "@media (prefers-reduced-motion: reduce)": {
                "&::before": {
                  animation: "none",
                  background:
                    "linear-gradient(135deg, #4A2CF5 0%, #775EFF 50%, #E84A8A 100%)",
                },
              },
            }
          : {}),
        ...(isGradientCard
          ? {
              boxShadow: "0 6px 20px rgba(74, 44, 245, 0.1)",
              border: "1.5px solid #4A2CF5",
            }
          : {}),
      }}
    >
      {isGradientCard ? (
        <Box
          aria-hidden="true"
          sx={{
            position: "absolute",
            bottom: 0,
            left: 0,
            right: 0,
            height: "48%",
            pointerEvents: "none",
            zIndex: 0,
            backgroundImage: ONBOARDING_GRADIENT,
            backgroundSize: "220% 100%",
            backgroundRepeat: "no-repeat",
            animation: "onboardingGradientDrift 14s ease-in-out infinite",
            "@keyframes onboardingGradientDrift": {
              "0%, 100%": { backgroundPosition: "0% 50%" },
              "50%": { backgroundPosition: "100% 50%" },
            },
            // Fade upward so the wash sits at the bottom of the card.
            maskImage: "linear-gradient(0deg, #000 28%, transparent 100%)",
            WebkitMaskImage:
              "linear-gradient(0deg, #000 28%, transparent 100%)",
            "@media (prefers-reduced-motion: reduce)": {
              animation: "none",
              backgroundPosition: "40% 50%",
            },
          }}
        />
      ) : null}

      <Box
        sx={{
          height: "100%",
          display: "flex",
          flexDirection: "column",
          borderRadius: recommended ? "9px" : "10px",
          border: recommended || isGradientCard ? "none" : "1px solid #E8EAF0",
          bgcolor: isGradientCard
            ? "transparent"
            : recommended
              ? "#FCFAFF"
              : "#FFFFFF",
          px: { xs: 1.75, md: 2 },
          pt: { xs: 1.75, md: 2 },
          pb: { xs: 1.5, md: 1.75 },
          position: "relative",
          boxSizing: "border-box",
          zIndex: 1,
        }}
      >
        {recommended ? (
          <Chip
            label="Recommended"
            size="small"
            data-testid={`pricing-recommended-badge-${plan.id}`}
            sx={{
              position: "absolute",
              top: 10,
              right: 10,
              height: 20,
              fontWeight: 700,
              fontSize: 10,
              borderRadius: "5px",
              bgcolor: colors.badgeBg,
              color: colors.badgeText,
              "& .MuiChip-label": { px: 0.9 },
            }}
          />
        ) : null}

        <Typography
          sx={{
            fontWeight: 700,
            fontSize: 15,
            lineHeight: "20px",
            letterSpacing: "-0.01em",
            color: colors.title,
            pr: recommended ? 9.5 : 0,
            mb: 0.75,
          }}
        >
          {plan.label}
        </Typography>

        <Typography
          sx={{
            fontSize: 11.5,
            lineHeight: 1.45,
            color: colors.description,
            mb: 2,
            minHeight: { md: 34 },
          }}
        >
          {plan.description}
        </Typography>

        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            flexWrap: "wrap",
            columnGap: 0.85,
            rowGap: 0.6,
            mb: 1.25,
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "baseline",
              columnGap: 0.6,
            }}
          >
            <Typography
              data-testid={`pricing-price-${plan.id}`}
              sx={{
                fontWeight: 800,
                fontSize: 24,
                lineHeight: 1.1,
                color: colors.price,
                letterSpacing: "-0.03em",
              }}
            >
              {priceLabel}
            </Typography>
            <Typography
              sx={{
                fontSize: 12,
                fontWeight: 500,
                color: colors.muted,
              }}
            >
              {billingLabel}
            </Typography>
          </Box>


        </Box>

        <Box
          data-testid={`pricing-trial-label-${plan.id}`}
          sx={{
            display: "inline-flex",
            alignItems: "center",
            alignSelf: "flex-start",
            width: "fit-content",
            maxWidth: "max-content",
            mb: plan.footnote ? 1.25 : 2,
            px: 1.25,
            py: 0.4,
            borderRadius: "999px",
            bgcolor: "#F7E7A3",
          }}
        >
          <Typography
            sx={{
              fontSize: 11.5,
              fontWeight: 700,
              color: "#8A6B12",
              letterSpacing: "0.02em",
              lineHeight: 1.25,
              whiteSpace: "nowrap",
              wordSpacing: "0.08em",
            }}
          >
            {offerLabel}
          </Typography>
        </Box>

        {plan.footnote ? (
          <Typography
            sx={{
              fontSize: 10.5,
              lineHeight: 1.4,
              color: colors.footnote,
              mb: 2,
            }}
          >
            {plan.footnote}
          </Typography>
        ) : null}

        <Box
          sx={{
            borderTop: `1px solid ${colors.divider}`,
            pt: 1.75,
            mb: 1.75,
            flex: "1 1 auto",
          }}
        >
          <Typography
            sx={{
              fontWeight: 700,
              fontSize: 11,
              letterSpacing: "0.01em",
              color: colors.featuresIntro,
              mb: 1.15,
            }}
          >
            {plan.featuresIntro}
          </Typography>

          {features.map((feature) => (
            <Box
              key={feature}
              sx={{
                display: "flex",
                alignItems: "flex-start",
                gap: 0.85,
                mb: 1,
                "&:last-of-type": { mb: 0 },
              }}
            >
              <CheckIcon
                sx={{
                  fontSize: 14,
                  color: colors.check,
                  mt: "1px",
                  flexShrink: 0,
                  opacity: 0.9,
                }}
              />
              <Typography
                sx={{
                  fontSize: 11.5,
                  lineHeight: 1.4,
                  color: colors.feature,
                }}
              >
                {feature}
              </Typography>
            </Box>
          ))}
        </Box>

        <Button
          fullWidth
          variant={recommended ? "contained" : "outlined"}
          disabled={disabled}
          onClick={() => onSelect(plan)}
          data-testid={`pricing-select-${plan.id}`}
          sx={{
            mt: "auto",
            minHeight: 34,
            py: 0.7,
            borderRadius: "7px",
            textTransform: "none",
            fontWeight: 700,
            fontSize: 12,
            boxShadow: "none",
            ...(recommended
              ? {
                  bgcolor: colors.ctaBg,
                  color: colors.ctaText,
                  "&:hover": {
                    bgcolor: colors.ctaHover,
                    boxShadow: "none",
                  },
                }
              : {
                  borderColor: "#D1D5DB",
                  color: "#111827",
                  bgcolor: "#FFFFFF",
                  "&:hover": {
                    borderColor: "#4A2CF5",
                    bgcolor: "#F6F4FF",
                  },
                }),
          }}
        >
          Select
        </Button>
      </Box>
    </Box>
  );
}
